import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../config.js';
import { FaCrown, FaInfoCircle, FaExclamationTriangle, FaCheckCircle, FaEnvelope, FaRobot, FaGem } from 'react-icons/fa';
import { BsStars } from 'react-icons/bs';
import { MdClose, MdPublic, MdBarChart } from 'react-icons/md';

const AgentCredits = ({ userToken }) => {
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCredits();
    }, []);

    const fetchCredits = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(`${apiBaseUrl}/api/agent/credits`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`
                }
            });

            if (response.data.success) {
                setCredits(response.data.premiumCredits);
            }
        } catch (err) {
            console.error('Error fetching credits:', err);
            setError(err.response?.data?.error || 'Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <div style={{
                    fontSize: '18px',
                    color: '#667eea'
                }}>
                    Cargando...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                padding: '40px 20px',
                textAlign: 'center'
            }}>
                <div style={{
                    fontSize: '48px',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    <MdClose size={48} color="#e74c3c" />
                </div>
                <div style={{
                    fontSize: '18px',
                    color: '#e74c3c',
                    marginBottom: '10px'
                }}>
                    {error}
                </div>
                <button
                    onClick={fetchCredits}
                    style={{
                        marginTop: '20px',
                        padding: '12px 24px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#2c3e50',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <FaCrown size={28} color="#667eea" />
                Mi Cuenta Premium
            </h2>

            {/* Credits Display Card */}
            <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                padding: '40px',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
                marginBottom: '40px'
            }}>
                <div style={{
                    fontSize: '18px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '15px',
                    fontWeight: '600'
                }}>
                    Valuaciones Premium Disponibles
                </div>
                <div style={{
                    fontSize: '72px',
                    fontWeight: 'bold',
                    color: 'white',
                    marginBottom: '15px'
                }}>
                    {credits}
                </div>
                <div style={{
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.8)'
                }}>
                    {credits === 1 ? 'valuación gratis' : 'valuaciones gratis'}
                </div>
            </div>

            {/* Information Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth > 768 ? 'repeat(2, 1fr)' : '1fr',
                gap: '20px',
                marginBottom: '40px'
            }}>
                {/* How it works */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '30px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{
                        fontSize: '40px',
                        marginBottom: '15px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <FaInfoCircle size={40} color="#667eea" />
                    </div>
                    <h3 style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: '15px'
                    }}>
                        ¿Cómo funciona?
                    </h3>
                    <ul style={{
                        textAlign: 'left',
                        color: '#5a6c7d',
                        lineHeight: '1.8',
                        paddingLeft: '20px'
                    }}>
                        <li>Cada valuación premium que realices consume 1 crédito</li>
                        <li>Se descuentan automáticamente al hacer valuaciones premium</li>
                        <li>Incluye análisis de mercado completo con IA</li>
                        <li>Resultados instantáneos sin necesidad de pago</li>
                    </ul>
                </div>

                {/* What's included */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '30px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{
                        fontSize: '40px',
                        marginBottom: '15px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <BsStars size={40} color="#ffc107" />
                    </div>
                    <h3 style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: '15px'
                    }}>
                        Valuación Premium Incluye
                    </h3>
                    <ul style={{
                        textAlign: 'left',
                        color: '#5a6c7d',
                        lineHeight: '1.8',
                        paddingLeft: '20px',
                        listStyle: 'none'
                    }}>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <FaRobot size={18} color="#667eea" />
                            Análisis con Inteligencia Artificial
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <MdPublic size={18} color="#667eea" />
                            Búsqueda en tiempo real en portales inmobiliarios
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <MdBarChart size={18} color="#667eea" />
                            Comparación con propiedades similares
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FaGem size={18} color="#667eea" />
                            Reporte detallado profesional
                        </li>
                    </ul>
                </div>
            </div>

            {/* Status Message */}
            {credits === 0 ? (
                <div style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid #ffc107',
                    marginBottom: '20px'
                }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                        <FaExclamationTriangle size={24} color="#856404" />
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                        Has usado todas tus valuaciones premium gratuitas
                    </div>
                    <div style={{ fontSize: '14px' }}>
                        Para continuar usando el servicio premium, deberás pagar por cada valuación
                    </div>
                </div>
            ) : (
                <div style={{
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid #28a745',
                    marginBottom: '20px'
                }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                        <FaCheckCircle size={24} color="#155724" />
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>
                        Tenés {credits} {credits === 1 ? 'valuación premium gratis' : 'valuaciones premium gratis'} disponibles
                    </div>
                </div>
            )}

            {/* Help Section */}
            <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                padding: '25px',
                textAlign: 'center',
                marginTop: '30px'
            }}>
                <div style={{
                    fontSize: '16px',
                    color: '#6c757d',
                    marginBottom: '15px'
                }}>
                    ¿Necesitás más valuaciones premium o tenés alguna consulta?
                </div>
                <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                }}>
                    <FaEnvelope size={18} />
                    info@tasacionesya.com
                </div>
            </div>
        </div>
    );
};

export default AgentCredits;
